import { atom } from 'jotai';

import { JobApplication, Variable, WorkerJobPositionStatus, JobApplicationStatusKey } from '@restworld/utility-types';

import { TimeLineKeysType, ApplicationStateType, timelineIdMapping } from './application-details.constants';

export type CandidatureDetailsType = {
  activeKey: TimeLineKeysType | '';
  openKeys: TimeLineKeysType[];
  completedKeys: TimeLineKeysType[];
  applyJobStatusData: Partial<JobApplication>;
  applyJobStatusDataLoading?: boolean;
  listWorkerJobApplicationsHistoryData: WorkerJobPositionStatus[];
  applicationState: ApplicationStateType;
  discardApplicationData: {
    workerDiscardedReasonVariable?: Variable[];
    discardData: {
      discardedBy?: 'worker';
      discardedStatusKey?: JobApplicationStatusKey;
      discardedOtherReason?: string;
      discardedReason?: string;
      selectedDiscardedReason?: string;
    };
    isApplicationDiscarded?: boolean;
    applicationDiscardSuccess?: boolean;
    loading?: boolean;
  };
  isSpontaneousApplication?: boolean;
};
const candidatureDetailsAtom = atom<CandidatureDetailsType>({
  activeKey: '',
  openKeys: [],
  completedKeys: [],
  applyJobStatusData: {},
  listWorkerJobApplicationsHistoryData: [],
  applicationState: 'pending',
  discardApplicationData: {
    discardData: {
      discardedBy: 'worker' as const,
      discardedStatusKey: 'worker_jp_discarded',
      discardedOtherReason: '',
      discardedReason: '',
      selectedDiscardedReason: '',
    },
  },
  isSpontaneousApplication: false,
});
candidatureDetailsAtom.debugLabel = 'candidatureDetailsAtom';

// `''` is a real value, not a placeholder: between a closed interview and the application
// actually reaching the company, no step is the current one.
export const setActiveKeyAtom = atom(null, (_get, set, activeKey: TimeLineKeysType | '') => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, activeKey }));
});
export const getActiveKeyAtom = atom((get) => get(candidatureDetailsAtom).activeKey);
// --- //
export const setOpenKeysAtom = atom(null, (_, set, keys: TimeLineKeysType[]) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, openKeys: keys }));
});
export const setOpenKeyAtom = atom(null, (_, set, key: TimeLineKeysType) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    openKeys: [...prev.openKeys, key],
  }));
});
export const getOpenKeysAtom = atom((get) => get(candidatureDetailsAtom).openKeys);
// --- //
export const setCompletedKeysAtom = atom(null, (_, set, completedKeys: TimeLineKeysType[]) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, completedKeys }));
});
export const getCompletedKeysAtom = atom((get) => get(candidatureDetailsAtom).completedKeys);
// --- //
export const setApplyJobStatusDataAtom = atom(null, (_, set, data: JobApplication) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, applyJobStatusData: data }));
});
export const getApplyJobStatusDataAtom = atom((get) => get(candidatureDetailsAtom).applyJobStatusData);
// -- //
export const setListWorkerJobApplicationsHistoryDataAtom = atom(null, (_, set, data: WorkerJobPositionStatus[]) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, listWorkerJobApplicationsHistoryData: data }));
});
export const getListWorkerJobApplicationsHistoryDataAtom = atom(
  (get) => get(candidatureDetailsAtom).listWorkerJobApplicationsHistoryData
);
// --//
export const applyJobStatusDataLoadingAtom = atom((get) => get(candidatureDetailsAtom).applyJobStatusDataLoading);
export const setApplyJobStatusDataLoadingAtom = atom(null, (_, set, loading: boolean) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, applyJobStatusDataLoading: loading }));
});
// -- //
export const setApplicationStateAtom = atom(null, (_, set, applicationState: ApplicationStateType) => {
  set(candidatureDetailsAtom, (prev) => ({ ...prev, applicationState }));
});
export const getApplicationStateAtom = atom((get) => get(candidatureDetailsAtom).applicationState);
// -- //
export const getDiscardApplicationDataAtom = atom((get) => get(candidatureDetailsAtom).discardApplicationData);
export const setWorkerDiscardedReasonVariableAtom = atom(null, (_, set, data: Variable[]) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: { ...prev.discardApplicationData, workerDiscardedReasonVariable: data },
  }));
});
export const getWorkerDiscardedReasonVariableAtom = atom(
  (get) => get(getDiscardApplicationDataAtom).workerDiscardedReasonVariable
);
export const setDiscardDataAtom = atom(
  null,
  (_, set, data: CandidatureDetailsType['discardApplicationData']['discardData']) => {
    set(candidatureDetailsAtom, (prev) => ({
      ...prev,
      discardApplicationData: { ...prev.discardApplicationData, discardData: data },
    }));
  }
);
export const getDiscardDataAtom = atom((get) => get(getDiscardApplicationDataAtom).discardData);
export const setSelectedDiscardedReasonAtom = atom(null, (_, set, selectedDiscardedReason: string) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: {
      ...prev.discardApplicationData,
      discardData: { ...prev.discardApplicationData.discardData, selectedDiscardedReason },
    },
  }));
});
export const getSelectedDiscardedReasonAtom = atom(
  (get) => get(getDiscardApplicationDataAtom).discardData.selectedDiscardedReason
);
export const setDiscardedOtherReasonAtom = atom(null, (_, set, discardedOtherReason: string) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: {
      ...prev.discardApplicationData,
      discardData: { ...prev.discardApplicationData.discardData, discardedOtherReason },
    },
  }));
});
export const getDiscardedOtherReasonAtom = atom(
  (get) => get(getDiscardApplicationDataAtom).discardData.discardedOtherReason
);
export const getApplicationDiscardSuccessAtom = atom(
  (get) => get(getDiscardApplicationDataAtom).applicationDiscardSuccess
);
export const setApplicationDiscardSuccessAtom = atom(null, (_, set, applicationDiscardSuccess: boolean) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: {
      ...prev.discardApplicationData,
      applicationDiscardSuccess,
    },
  }));
});
export const getIsApplicationDiscardedAtom = atom((get) => get(getDiscardApplicationDataAtom).isApplicationDiscarded);
export const setIsApplicationDiscardedAtom = atom(null, (_, set, isApplicationDiscarded: boolean) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: {
      ...prev.discardApplicationData,
      isApplicationDiscarded,
    },
  }));
});
export const applicationDiscardLoadingAtom = atom((get) => get(getDiscardApplicationDataAtom).loading);
export const setApplicationDiscardLoadingAtom = atom(null, (_, set, loading: boolean) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    discardApplicationData: {
      ...prev.discardApplicationData,
      loading,
    },
  }));
});
export const accomplishedStepsBeforeDiscardedAtom = atom((get) => {
  const applyJobStatusData = get(getApplyJobStatusDataAtom);
  const { discarded_from_status_key: discardedFromStatusKey } = applyJobStatusData;
  const accomplishedStepsBeforeDiscarded = Object.keys(timelineIdMapping).reduce((prev, curr, index, list) => {
    const keys = timelineIdMapping[curr];
    const isKeyIncluded = keys.includes(discardedFromStatusKey);
    // push current and keys before the current key
    if (isKeyIncluded) {
      const keysBeforeCurr = list.slice(0, index);
      return [...prev, ...keysBeforeCurr, curr];
    }
    return prev;
  }, [] as TimeLineKeysType[]);

  return accomplishedStepsBeforeDiscarded;
});
export const accomplishedStepsAtom = atom((get) => {
  const applyJobStatusData = get(getApplyJobStatusDataAtom);
  const { status_key: statusKey } = applyJobStatusData;
  const accomplishedStepsBeforeDiscarded = Object.keys(timelineIdMapping).reduce((prev, curr, index, list) => {
    const keys = timelineIdMapping[curr];
    const isKeyIncluded = keys.includes(statusKey);
    // push current and keys before the current key
    if (isKeyIncluded) {
      const keysBeforeCurr = list.slice(0, index);
      return [...prev, ...keysBeforeCurr];
    }
    return prev;
  }, [] as TimeLineKeysType[]);
  return accomplishedStepsBeforeDiscarded;
});
// -- //
export const getIsSpontaneousApplicationAtom = atom((get) => get(candidatureDetailsAtom).isSpontaneousApplication);
export const setIsSpontaneousApplicationAtom = atom(null, (_, set, isSpontaneousApplication: boolean) => {
  set(candidatureDetailsAtom, (prev) => ({
    ...prev,
    isSpontaneousApplication,
  }));
});
export const applicationSentimentAndStatusAtom = atom((get) => {
  const applicationDetails = get(candidatureDetailsAtom);
  return {
    statusKey: applicationDetails.applyJobStatusData?.status_key,
    sentiment: applicationDetails.applyJobStatusData?.status?.sentiment,
  };
});

export const isRestaurantPublicAtom = atom((get) => {
  const applicationDetails = get(candidatureDetailsAtom);
  if (applicationDetails.applyJobStatusData?.restaurant_id) {
    return applicationDetails.applyJobStatusData?.restaurant?.show_details ?? false;
  }
  return applicationDetails?.applyJobStatusData?.job_position?.restaurant?.show_details ?? false;
});
